// /c:/Users/himan/intern/another/tele_frontend_2/src/views/admin/doctors/patient-details/components/SaveTemplateModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PrescriptionCard, formatDate } from "views/admin/doctors/PastVisitsModal.js";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Input,
  Textarea,
  Divider,
  Box,
  Alert,
  AlertIcon,

  useToast,

} from 'components-lib';
import { axiosInstance } from 'config/api';

const LOCAL_STORAGE_KEY = 'doctor_templates';
const CURRENT_FORM_KEY = 'currentPatientFormData';

// Utilities
const pad2 = (n) => String(n).padStart(2, '0');
const formatDateYMD = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};
const formatDateDMY = (d) => {
  const dt = d ? new Date(d) : new Date();
  return `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
};
const humanTimestampDMY = (d) => {
  const dt = d ? new Date(d) : new Date();
  const time = dt.toLocaleTimeString(undefined, { hour12: true }).toLowerCase();
  return `${formatDateDMY(dt)}, ${time}`;
};

const getDoctorId = (form) => {
  // Prefer explicit doctor info in form; else try common localStorage keys
  const fromForm = form?.doctor_id || form?.doctor?.id || form?.doctorId || form?.doctor?.doctor_id;
  if (fromForm) return fromForm;
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user?.id) return user.id;
    if (user?.doctor_id) return user.doctor_id;
  } catch { }
  try {
    const doctor = JSON.parse(localStorage.getItem('doctor') || 'null');
    if (doctor?.id) return doctor.id;
    if (doctor?.doctor_id) return doctor.doctor_id;
  } catch { }
  return null;
};

const parseFromTo = (fromTo) => {
  if (!fromTo || typeof fromTo !== 'string') return { from: '', to: '' };
  const parts = fromTo.split(' - ');
  const from = parts[0]?.trim();
  const to = parts[1]?.trim();
  return { from: formatDateYMD(from), to: formatDateYMD(to) };
};;

const SaveTemplateModal = ({ isOpen, onClose,
  doctor_id }) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [currentForm, setCurrentForm] = useState(null);

  const buildTemplatePayload = useCallback((form = {}) => {
    const today = new Date();
    const nowYMD = formatDateYMD(today);

    // medicines
    const medicinesArr = Array.isArray(form.medicinesData) ? form.medicinesData : [];
    const medicines = medicinesArr.map((m, idx) => {
      const { from, to } = parseFromTo(m.fromTo);
      return {
        medicine_no: idx + 1,
        name: m.name || '',
        type: m.type || '',
        dosage: m.dosage || '',
        frequency: Array.isArray(m.frequency) ? m.frequency.join(', ') : (m.frequency || ''),
        duration: m.duration || '',
        when_to_take: m.when_to_take || m.howToTake || '',
        from_date: from || nowYMD,
        to_date: to || nowYMD
      };
    });

    // advice
    const advicesArr = Array.isArray(form.advicesData) ? form.advicesData : [];
    const advice = advicesArr.map((a) => ({
      advice_text: a.advice || a.text || '',
      date: formatDateYMD(a.date) || nowYMD
    }));

    // complaints
    const complaintsArr = Array.isArray(form.complaints) ? form.complaints : [];
    const complaints = complaintsArr.map((c, idx) => ({
      complaint_no: idx + 1,
      complaint: c.complaint || '',
      severity: c.severity || '',
      duration: c.duration || '',
      date: formatDateYMD(c.date) || nowYMD
    }));

    // diagnosis
    const diagnosisArr = Array.isArray(form.diagnosis) ? form.diagnosis : [];
    const diagnosis = diagnosisArr.map((d, idx) => ({
      diagnosis_no: idx + 1,
      diagnosis: d.diagnosis || '',
      duration: d.duration || '',
      date: formatDateYMD(d.date) || nowYMD
    }));

    // clinical findings
    const findingsArr = Array.isArray(form.clinicalFindingsData) ? form.clinicalFindingsData : [];
    const clinical_findings = findingsArr.map((f, idx) => ({
      finding_no: idx + 1,
      finding: f.finding || f.text || '',
      date: nowYMD
    }));
    const rawPastHistory = Array.isArray(form.PastHistoryData)
      ? form.PastHistoryData
      : Array.isArray(form.past_history)
        ? form.past_history
        : [];
    const past_history = rawPastHistory.map((h, idx) => ({
      history_no: h.history_no || h.pasthistory_no || idx + 1,
      history: h.history || h.pasthistory || '',
      severity: h.severity || '',
      duration: h.duration || '',
      date: formatDateYMD(h.date) || nowYMD,
    }));
    // allergies
    const allergies = (() => {
      const fromArray = (list) =>
        list
          .filter(Boolean)
          .map((a, idx) => ({
            allergy_no: a.allergy_no || idx + 1,
            medicines: a.medicines || a.medicine || '',
            food: a.food || '',
            others: a.others || a.other || '',
          }));
      if (Array.isArray(form.allergies) && form.allergies.length) {
        return fromArray(form.allergies);
      }
      const raw = form.allergies && typeof form.allergies === 'object' ? form.allergies : {};
      const consolidated = {
        medicines:
          raw.medicines ||
          raw.medicine ||
          form.medicines ||
          form.medicineAllergy ||
          '',
        food: raw.food || form.food || form.foodAllergy || '',
        others:
          raw.others ||
          raw.other ||
          form.other ||
          form.otherAllergy ||
          '',
      };
      return Object.values(consolidated).some(Boolean)
        ? [{ allergy_no: 1, ...consolidated }]
        : [];
    })();

    // referred to (use single entry if present in form)
    const referred_to = form.referredDoctor
      ? [{
        referred_doctor_name: form.referredDoctor || '',
        speciality: form.referredSpeciality || form.referredSpecialty || '',
        doctor_phone: form.referredPhone || '',
        doctor_email: form.referredEmail || '',
        hospital: form.referredHospital || '',
        hospital_address: form.referredHospitalAddress || '',
        phone: form.referredPhone || '',
        email: form.referredEmail || '',
        date: nowYMD
      }]
      : [];

    // next visit
    const next_visit = form.nextVisitDate ? formatDateYMD(form.nextVisitDate) : '';

    // investigations
    const investigation = Array.isArray(form.investigationsList)
      ? form.investigationsList.filter(Boolean).join(', ')
      : (form.investigation || '');

    // past medication
    const past_medication = form.pastMedication || '';

    const template_name = `Template ${formatDateDMY(today)}`; // e.g., Template 23/9/2025
    const template_description = `Auto-generated template created on ${humanTimestampDMY(today)}`;

    return {
      template_name,
      template_description,
      doctor_id: getDoctorId(form) ||
        doctor_id,
      medicines,
      advice,
      complaints,
      diagnosis,
      clinical_findings,
      referred_to,
      next_visit,
      investigation,
      past_medication,
      past_history,
      allergies,
    };
  }, [doctor_id]);

  const loadCurrentForm = useCallback(() => {
    try {
      const raw = localStorage.getItem(CURRENT_FORM_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      setCurrentForm(parsed || {});
      const draft = buildTemplatePayload(parsed || {});
      setTemplateName(draft.template_name || '');
      setTemplateDescription(draft.template_description || '');
    } catch (e) {
      setCurrentForm({});
    }
  }, [buildTemplatePayload]);

  useEffect(() => {
    if (isOpen) {
      loadCurrentForm();
    } else {
      setTemplateName('');
      setTemplateDescription('');
      setCurrentForm(null);
    }
  }, [isOpen, loadCurrentForm]);

  const previewPayload = useMemo(
    () => buildTemplatePayload(currentForm || {}),
    [currentForm, buildTemplatePayload]
  );

  const previewCardData = useMemo(() => {
    if (!previewPayload) return null;
    const safeList = (list, mapFn) =>
      Array.isArray(list) ? list.map(mapFn) : [];
    const fallbackDate =
      previewPayload.next_visit ||
      currentForm?.appointment?.date ||
      currentForm?.appointmentDate ||
      new Date().toISOString();
    
    
    const allergiesList = (() => {
      const normalizeField = (value) =>
        Array.isArray(value) ? value.filter(Boolean).join(', ') : (value || '');
      const buildNormalizedList = (list = []) =>
        list.map((entry) => ({
          ...entry,
          medicines: normalizeField(entry.medicines || entry.medicine),
          food: normalizeField(entry.food),
          others: normalizeField(entry.others || entry.other),
        }));

      if (Array.isArray(previewPayload.allergies) && previewPayload.allergies.length) {
        return buildNormalizedList(previewPayload.allergies);
      }
      if (Array.isArray(currentForm?.allergies) && currentForm.allergies.length) {
        return buildNormalizedList(currentForm.allergies);
      }
      if (currentForm?.allergies && typeof currentForm.allergies === 'object') {
        const obj = currentForm.allergies;
        if (obj.food || obj.medicines || obj.others || obj.other) {
          return buildNormalizedList([
            {
              medicines: obj.medicines || obj.medicine || '',
              food: obj.food || '',
              others: obj.others || obj.other || '',
            },
          ]);
        }
      }
      const direct = {
        medicines: currentForm?.medicines || '',
        food: currentForm?.food || '',
        others: currentForm?.others || currentForm?.other || '',
      };
      return Object.values(direct).some(Boolean) ? buildNormalizedList([direct]) : [];
    })();

    return {
      appointment: {
        date: fallbackDate,
        time: currentForm?.appointment?.time || currentForm?.appointmentTime || '',
      },

      doctor: {
        name: currentForm?.doctor?.name || currentForm?.doctorName || '',
      },
      doctor_id: currentForm?.doctor?.id || currentForm?.doctorId || '',
      patient: {
        name: currentForm?.patient?.name || currentForm?.patientName || '',
        id: currentForm?.patient?.id || currentForm?.patientId || '',
      },
      clinic: {
        name: currentForm?.clinic?.name || currentForm?.clinicName || '',
      },

      
      prescription: {
        next_visit: previewPayload.next_visit || '',
        investigation: previewPayload.investigation || '',
        past_medication: previewPayload.past_medication || '',
      },
      diagnosis: safeList(previewPayload.diagnosis, (d, idx) => ({
        ...d,
        id: d.id || d.diagnosis_no || idx,
      })),
      complaints: safeList(previewPayload.complaints, (c, idx) => ({
        ...c,
        id: c.id || c.complaint_no || idx,
      })),
      medicines: safeList(previewPayload.medicines, (m, idx) => ({
        ...m,
        id: m.id || m.medicine_no || idx,
      })),
      advice: safeList(previewPayload.advice, (a, idx) => ({
        ...a,
        id: a.id || idx,
      })),
      clinical_findings: safeList(previewPayload.clinical_findings, (f, idx) => ({
        ...f,
        id: f.id || f.finding_no || idx,
      })),
      referred_to: safeList(previewPayload.referred_to, (r, idx) => ({
        ...r,
        id: r.id || idx,
      })),
      // past_history: safeList(previewPayload.past_history, (h, idx) => ({
      //   ...h,
      //   id: h.id || h.history_no || h.pasthistory_no || idx,
      // })),
      // allergies: safeList(allergiesList, (a, idx) => ({
      //   ...a,
      //   id: a.id || a.allergy_no || idx,
      // })),
    };
  }, [previewPayload, currentForm]);

  const hasContent = useMemo(() => {
    if (!previewPayload) return false;
    return Boolean(
      (previewPayload.medicines && previewPayload.medicines.length) ||
      (previewPayload.advice && previewPayload.advice.length) ||
      (previewPayload.complaints && previewPayload.complaints.length) ||
      (previewPayload.diagnosis && previewPayload.diagnosis.length) ||
      (previewPayload.clinical_findings && previewPayload.clinical_findings.length) ||
      (previewPayload.past_history && previewPayload.past_history.length)
    );
  }, [previewPayload]);

  const saveTemplate = async () => {
    if (!previewPayload) return;
    if (!templateName.trim()) {
      toast({ title: 'Validation', description: 'Template name is required', status: 'warning', duration: 2500, isClosable: true });
      return;
    }

    const payload = {
      ...previewPayload,
      template_name: templateName.trim(),
      template_description: templateDescription.trim() || previewPayload.template_description
    };

    setIsLoading(true);
    try {
      await axiosInstance.post('api/templates/create', payload);
      toast({ title: 'Success', description: 'Template saved successfully', status: 'success', duration: 3000, isClosable: true });
      onClose();
    } catch (err) {
      try {
        // fallback to local storage
        const local = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '[]');
        local.unshift({ _id: `local-${Date.now()}`, createdAt: new Date().toISOString(), ...payload });
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(local));
        toast({ title: 'Saved locally', description: 'Server save failed; saved to device', status: 'warning', duration: 3000, isClosable: true });
        onClose();
      } catch (e) {
        toast({ title: 'Error', description: 'Failed to save template', status: 'error', duration: 3000, isClosable: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" height="auto"
      scrollBehavior='inside'
      isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Save as Template</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {!currentForm && (
              <Alert status="warning">
                <AlertIcon />
                No consultation data found in localStorage under "{CURRENT_FORM_KEY}".
              </Alert>
            )}

            {!hasContent && (
              <Alert status="warning">
                <AlertIcon />
                No consultation data to save. Please add medicines, advice, or complaints before saving as template.
              </Alert>
            )}

            <Box>
              <Text fontWeight="bold" mb={2}>Template Name: *</Text>
              <Input
                placeholder="Enter template name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2}>Template Description:</Text>
              <Textarea
                placeholder="Enter template description"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
              />
            </Box>

            <Divider />

            <Box>
              <Text fontWeight="bold" mb={2}>Content Preview</Text>
              {hasContent && previewCardData && (
                <PrescriptionCard data={previewCardData} formatDate={formatDate} isallergies={false} ispasthistory={false} />
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={saveTemplate} isLoading={isLoading} isDisabled={!hasContent || !templateName.trim()}>
            Save Template
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SaveTemplateModal;
